import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Text } from 'office-ui-fabric-react/lib/Text';
import range from 'lodash/range';
import head from 'lodash/head';
import last from 'lodash/last';
import clsx from 'clsx';
import Dropdown from '../Dropdown';
import styles from './Pagination.style';

const Pagination = ({
  totalRecords,
  pageLimit,
  handlePageChange,
  className: classNameProp,
  resetPagination,
  currentPageVal = 0,
  ...other
}) => {
  const className = clsx(styles.root, classNameProp);
  const [currentPage, setCurrentPage] = useState(currentPageVal);
  const totalPages = Math.ceil(totalRecords / pageLimit);
  const pageRange = range(1, totalPages + 1);
  const pages = pageRange.map((page, index) => ({
    key: index,
    text: `${page}`,
  }));
  const isEmpty = Number.isNaN(totalPages) || totalPages === 0;

  /**
   *  Reset current page when the current page index is greater than zero.
   *  Situation arises while searching/filtering the data. Component should adjust or reset the starting index of current page when it greater than zero
   */
  if (currentPage !== 0 && resetPagination) {
    setCurrentPage(0);
  }

  const gotoPage = pageIndex => {
    const currentPageIndex = Math.max(0, Math.min(pageIndex, totalPages));
    const paginationData = {
      currentPageIndex,
      totalPages,
      pageLimit,
      totalRecords,
    };
    setCurrentPage(currentPageIndex);
    handlePageChange(paginationData);
  };

  const handleChange = (event, page) => {
    gotoPage(page.key);
  };

  const handleMoveLeft = () => {
    gotoPage(currentPage - 1);
  };

  const handleMoveRight = () => {
    gotoPage(currentPage + 1);
  };

  const handleFirst = () => {
    gotoPage(head(pages).key);
  };

  const handleLast = () => {
    gotoPage(last(pages).key);
  };

  return (
    <>
      {!isEmpty && (
        <Stack
          key="0"
          horizontal
          nowrap
          horizontalAlign="start"
          verticalAlign="center"
          className={className}
          {...other}
        >
          <Stack.Item>
            <IconButton
              iconProps={{ iconName: 'DoubleChevronLeft' }}
              className={styles.first}
              ariaLabel="First"
              onClick={handleFirst}
              disabled={currentPage === head(pages)?.key}
            />
          </Stack.Item>
          <Stack.Item>
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              className={styles.previous}
              ariaLabel="Previous"
              onClick={handleMoveLeft}
              disabled={currentPage === head(pages)?.key}
            />
          </Stack.Item>
          <Stack.Item>
            <Dropdown
              aria-label="Pagination Dropdown"
              field={{ name: 'pagination', value: [currentPage] }}
              label=""
              options={pages}
              className={styles.pageNumber}
              onChange={handleChange}
              disabled={isEmpty}
            />
          </Stack.Item>
          <Stack.Item>
            <Text as="p" block variant="small" className={styles.totalPages}>
              of {totalPages}
            </Text>{' '}
          </Stack.Item>
          <Stack.Item>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              className={styles.next}
              ariaLabel="Next"
              onClick={handleMoveRight}
              disabled={currentPage === last(pages)?.key}
            />
          </Stack.Item>
          <Stack.Item>
            <IconButton
              iconProps={{ iconName: 'DoubleChevronRight' }}
              className={styles.last}
              ariaLabel="Last"
              onClick={handleLast}
              disabled={currentPage === last(pages)?.key}
            />
          </Stack.Item>
        </Stack>
      )}
    </>
  );
};

Pagination.propTypes = {
  totalRecords: PropTypes.number.isRequired,
  pageLimit: PropTypes.number,
  handlePageChange: PropTypes.func,
  className: PropTypes.string,
  resetPagination: PropTypes.bool,
  currentPageVal: PropTypes.number,
};

Pagination.defaultProps = {
  pageLimit: 20,
  className: '',
  handlePageChange: null,
  resetPagination: false,
  currentPageVal: 0,
};

export default Pagination;
